import React, { useRef, useEffect, useState } from 'react';
import { Box, Dialog, DialogContent, IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import axiosInstance from '../../axios.config';

const CertificateModal = ({ open, onClose, certificateImageUrl, overlayText, courseName, lngsltd, userQuizID, userID, quizID, startTime, endTime, courseid }) => {
    const canvasRef = useRef(null);
    const [isCertificateReady, setIsCertificateReady] = useState(false);

    useEffect(() => {
        if (open) {
            setIsCertificateReady(false); // Reset the state when modal opens

            // Slight delay to ensure canvas is mounted before drawing
            setTimeout(() => {
                drawCertificate();
            }, 100); // 100ms delay
        }
    }, [open, certificateImageUrl, overlayText]);

    const drawCertificate = () => {
        const canvas = canvasRef.current;
        if (!canvas) {
            console.error('Canvas or context not available.');
            return;
        }

        const ctx = canvas.getContext('2d');
        const image = new Image();

        // Handle potential cross-origin issues
        image.crossOrigin = 'anonymous';
        image.src = certificateImageUrl;

        image.onload = () => {
            // Set canvas dimensions equal to the image dimensions
            canvas.width = image.width;
            canvas.height = image.height;

            // Clear canvas before redrawing
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Draw the certificate image
            ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

            // Add overlay text
            ctx.font = '100px Quattrocento';
            ctx.fillStyle = 'black';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.textRendering = 'optimizeLegibility';
            // Adjust the y-coordinate to move the text down
            const textX = canvas.width / 2;
            const textY = canvas.height / 2 + 60; // Move text 50px down

            ctx.fillText(overlayText, textX, textY);

            // Draw course name
            ctx.font = 'bold 50px "Open Sans"';
            ctx.fillStyle = 'black';
            ctx.fillText(courseName, textX, textY + 200);

            setIsCertificateReady(true); // Enable the download button 

             // Automatically upload the certificate once it's ready
             handleCertificateUpload();
            
            
        };

        image.onerror = (error) => {
            console.error('Failed to load the certificate image.', error);
        };
    };

    const handleDownload = () => {
        const canvas = canvasRef.current;
        if (!canvas) {
            console.error('Canvas is not available for download.');
            return;
        }

        const dataUrl = canvas.toDataURL('image/png');
        if (dataUrl) {
            const link = document.createElement('a');
            link.href = dataUrl; // Convert canvas to image URL
            link.download = overlayText+'_Certificate.png'; // Desired filename
            link.click();
        } else {
            console.error('Failed to generate canvas data URL.');
        }
    };

    // Automatically upload the certificate
    const handleCertificateUpload = async () => {

        // console.log("Values of CourseID, QuizID: ", quizID, courseid)
        const canvas = canvasRef.current;
        if (!canvas) {
            console.error('Canvas is not available for upload.');
            return;
        }        
        // Convert canvas to a blob (image file)
        canvas.toBlob(async (blob) => {
            if (!blob) {
                console.error('Failed to convert canvas to blob.');
                return;
            }

            // Prepare form data with blob (image file)
            const formData = new FormData();
            formData.append('Certificate', blob, `${overlayText}_Certificate.png`);  // Append file to FormData
            formData.append('UserID', userID);
            formData.append('CourseID', courseid);
            formData.append('QuizID', quizID);
            formData.append('IssueDate', new Date().toISOString());  // Use current date as issue date            
            try {
                const response = await axiosInstance.post(`/Certificate/uploadcertificate`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
                // console.log('Certificate uploaded successfully:', response.data);
            } catch (error) {
                console.error('Error uploading certificate:', error);
            }
        }, 'image/png');
    };


    return (
        <Dialog
            open={open}
            // onClose={onClose}
            onClose={(event, reason) => {
                // Prevent closing if the click is outside or by pressing Escape
                if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                    onClose();
                }
            }}
            maxWidth="md"
            fullWidth
            disableEscapeKeyDown
            sx={{
                overflowY: 'auto', // Enable scrolling for the dialog on smaller screens
            }}
        >
            <DialogContent
                sx={{
                    padding: { xs: '4px', sm: '8px' }, // Reduced padding for mobile devices
                    position: 'relative', // Ensure icons are relative to the content
                }}
            >
                <Box
                    p={3}
                    textAlign="center"
                    sx={{
                        maxWidth: '800px',
                        margin: 'auto',
                        backgroundColor: '#f3f3f3',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                        position: 'relative',
                        mt: { xs: '10px', sm: '0' }, // Adjust margin-top to move the certificate up on mobile
                    }}
                >
                    {/* Close and Download icons positioned at top-right with tooltips */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: { xs: 0, sm: 10 },  // Adjust top spacing for mobile
                            right: { xs: 0, sm: 10 }, // Adjust right positioning for mobile
                            display: 'flex',
                            gap: 1,
                            zIndex: 10, // Ensure icons are on top of the certificate
                        }}
                    >
                        <Tooltip title={lngsltd["Download"]}>
                            <IconButton
                                onClick={handleDownload}
                                size="small"
                                color="primary"
                                disabled={!isCertificateReady} // Disable download until certificate is ready
                                sx={{
                                    border: '1px solid #ccc', // Add a border
                                    borderRadius: '4px', // Make the corners slightly rounded
                                    padding: { xs: '1px', sm: '2px' }, // Add some padding
                                    backgroundColor: '#fff',
                                    fontSize: { xs: '8px', sm: '12px' }, // Adjust icon size for mobile
                                }}
                            >
                                <DownloadIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={lngsltd["Close"]}>
                            <IconButton
                                onClick={onClose}
                                size="small"
                                color="secondary"
                                sx={{
                                    border: '1px solid #ccc', // Add a border
                                    borderRadius: '4px', // Make the corners slightly rounded
                                    padding: { xs: '1px', sm: '2px' }, // Add some padding
                                    backgroundColor: '#fff',
                                    fontSize: { xs: '8px', sm: '12px' }, // Adjust icon size for mobile
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>

                    </Box>

                    <Box
                        sx={{
                            position: 'relative',
                            display: 'inline-block',
                            maxWidth: '100%',
                            marginBottom: '1rem',
                            mt: { xs: '15px', sm: '0' }, // Adjust margin-top for mobile devices to avoid overlap
                        }}
                    >
                        {/* Visible canvas */}
                        <canvas
                            ref={canvasRef}
                            style={{
                                border: '1px solid black',
                                width: '100%',
                                height: {
                                    xs: '300px',  // Extra small screens
                                    sm: '350px',  // Small screens
                                    md: '400px',  // Medium screens
                                    lg: '400px',  // Large screens
                                },
                                maxHeight: '400px', // Restrict height on smaller screens
                                display: 'block',
                            }}
                        />
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default CertificateModal;
